import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronRight } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';

/**
 * Left-side chart options: Distribución + Variable + Metric + Filter menus.
 */
const ChartOptionsMenu = ({
  axisModes = [],
  axisKey,
  onAxisKeyChange,
  dimensions = [],
  stackKey,
  onStackKeyChange,
  metrics = [],
  metricKey,
  onMetricKeyChange,
  filterSections = [],
}) => {
  const ui = useUIColors();
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null); // 'axis' | 'variable' | 'metric' | 'filter' | null
  /** Filter subsection collapse: key -> true when collapsed. */
  const [collapsedFilterSections, setCollapsedFilterSections] = useState({});

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setExpanded(null);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setExpanded(null);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const border = ui.border;
  const panel = ui.panel;
  const text = ui.text;
  const muted = ui.muted;
  const headerBg = ui.header;
  const activeBg = `color-mix(in srgb, ${ui.interactive} 14%, ${panel})`;

  const itemBtn = (isActive = false) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    width: '100%',
    border: 'none',
    background: isActive ? activeBg : 'transparent',
    color: text,
    fontSize: '12px',
    fontWeight: 600,
    padding: '8px 10px',
    cursor: 'pointer',
    textAlign: 'left',
  });

  const toggleExpanded = (key) => {
    setExpanded((prev) => (prev === key ? null : key));
  };

  const toggleFilterSection = (sectionKey) => {
    setCollapsedFilterSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const isFilterSectionOpen = (sectionKey) => collapsedFilterSections[sectionKey] !== true;

  const hasAxis = axisModes.length > 1;
  const hasDimensions = dimensions.length > 0;
  const hasMetrics = metrics.length > 0;
  const hasFilters = filterSections.length > 0;

  const sectionBorderTop = (before) => (before ? { borderTop: `1px solid ${border}` } : null);

  const renderNumericRangeControls = (section) => {
    const config = section.config || {};
    const fields = Array.isArray(section.fields) && section.fields.length
      ? section.fields
      : [
          { key: 'minMillions', label: 'Desde (M)', min: 0 },
          { key: 'maxMillions', label: 'Hasta (M)', min: 0 },
          { key: 'stepMillions', label: 'Tamaño (M)', min: 0.1 },
        ];
    const hint =
      section.hint ||
      'Límites y tamaño de rangos en millones de CLP (c/IVA).';
    return (
      <div
        style={{
          display: 'grid',
          gap: '8px',
          padding: '10px 12px 12px',
          background: panel,
          borderBottom: `1px solid color-mix(in srgb, ${border} 70%, transparent)`,
        }}
      >
        <div style={{ fontSize: '11px', color: muted, lineHeight: 1.35 }}>{hint}</div>
        {fields.map((field) => (
          <label
            key={field.key}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 88px',
              alignItems: 'center',
              gap: '8px',
              fontSize: '12px',
              fontWeight: 600,
              color: text,
            }}
          >
            <span>{field.label}</span>
            <input
              type="number"
              min={field.min ?? 0}
              step="any"
              value={config[field.key] ?? ''}
              onChange={(event) => {
                const raw = event.target.value;
                const next = raw === '' ? '' : Number(raw);
                section.onChange?.({ [field.key]: next });
              }}
              onBlur={() => section.onCommit?.()}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  section.onCommit?.();
                }
              }}
              style={{
                width: '100%',
                border: `1px solid ${border}`,
                borderRadius: '6px',
                background: headerBg,
                color: text,
                fontSize: '12px',
                fontWeight: 600,
                padding: '6px 8px',
              }}
            />
          </label>
        ))}
      </div>
    );
  };

  const isNumericRangeSection = (section) =>
    section?.type === 'numericRange' || section?.type === 'amountRange';

  return (
    <div ref={rootRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        aria-label="Opciones del gráfico"
        aria-expanded={open}
        onClick={() => {
          setOpen((prev) => !prev);
          setExpanded(null);
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '28px',
          height: '28px',
          borderRadius: '7px',
          border: `1px solid ${border}`,
          background: open ? activeBg : panel,
          color: text,
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <ChevronDown size={16} strokeWidth={2.25} />
      </button>

      {open ? (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '4px',
            minWidth: '220px',
            maxWidth: '300px',
            maxHeight: '400px',
            overflowY: 'auto',
            background: panel,
            border: `1px solid ${border}`,
            borderRadius: '8px',
            boxShadow: ui.shadowMd || '0 8px 24px rgba(0,0,0,0.12)',
            zIndex: 50,
          }}
        >
          {hasAxis ? (
            <div>
              <button
                type="button"
                role="menuitem"
                onClick={() => toggleExpanded('axis')}
                style={itemBtn(expanded === 'axis')}
              >
                <span>Distribución</span>
                <ChevronRight
                  size={14}
                  color={muted}
                  style={{
                    transform: expanded === 'axis' ? 'rotate(90deg)' : 'none',
                    transition: 'transform 0.15s ease',
                  }}
                />
              </button>
              {expanded === 'axis' ? (
                <div role="menu" style={{ borderTop: `1px solid ${border}`, background: headerBg }}>
                  <div
                    style={{
                      padding: '6px 10px',
                      fontSize: '10px',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: muted,
                    }}
                  >
                    Eje X
                  </div>
                  {axisModes.map((mode) => {
                    const selected = mode.key === axisKey;
                    return (
                      <button
                        key={mode.key}
                        type="button"
                        role="menuitemradio"
                        aria-checked={selected}
                        onClick={() => onAxisKeyChange?.(mode.key)}
                        style={{
                          ...itemBtn(selected),
                          paddingLeft: '18px',
                          background: selected ? activeBg : panel,
                        }}
                      >
                        <span>{mode.label}</span>
                        {selected ? <Check size={14} color={ui.interactive} /> : <span style={{ width: 14 }} />}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}

          {hasDimensions ? (
            <div style={sectionBorderTop(hasAxis)}>
              <button
                type="button"
                role="menuitem"
                onClick={() => toggleExpanded('variable')}
                style={itemBtn(expanded === 'variable')}
              >
                <span>Variable</span>
                <ChevronRight
                  size={14}
                  color={muted}
                  style={{
                    transform: expanded === 'variable' ? 'rotate(90deg)' : 'none',
                    transition: 'transform 0.15s ease',
                  }}
                />
              </button>
              {expanded === 'variable' ? (
                <div role="menu" style={{ borderTop: `1px solid ${border}`, background: headerBg }}>
                  <div
                    style={{
                      padding: '6px 10px',
                      fontSize: '10px',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: muted,
                    }}
                  >
                    Visualizar por
                  </div>
                  {dimensions.map((dimension) => {
                    const selected = dimension.key === stackKey;
                    return (
                      <button
                        key={dimension.key}
                        type="button"
                        role="menuitemradio"
                        aria-checked={selected}
                        onClick={() => onStackKeyChange?.(dimension.key)}
                        style={{
                          ...itemBtn(selected),
                          paddingLeft: '18px',
                          background: selected ? activeBg : panel,
                        }}
                      >
                        <span>{dimension.label}</span>
                        {selected ? <Check size={14} color={ui.interactive} /> : <span style={{ width: 14 }} />}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}

          {hasMetrics ? (
            <div style={sectionBorderTop(hasAxis || hasDimensions)}>
              <button
                type="button"
                role="menuitem"
                onClick={() => toggleExpanded('metric')}
                style={itemBtn(expanded === 'metric')}
              >
                <span>Eje</span>
                <ChevronRight
                  size={14}
                  color={muted}
                  style={{
                    transform: expanded === 'metric' ? 'rotate(90deg)' : 'none',
                    transition: 'transform 0.15s ease',
                  }}
                />
              </button>
              {expanded === 'metric' ? (
                <div role="menu" style={{ borderTop: `1px solid ${border}`, background: headerBg }}>
                  <div
                    style={{
                      padding: '6px 10px',
                      fontSize: '10px',
                      fontWeight: 700,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: muted,
                    }}
                  >
                    Mostrar por
                  </div>
                  {metrics.map((metric) => {
                    const selected = metric.key === metricKey;
                    return (
                      <button
                        key={metric.key}
                        type="button"
                        role="menuitemradio"
                        aria-checked={selected}
                        onClick={() => onMetricKeyChange?.(metric.key)}
                        style={{
                          ...itemBtn(selected),
                          paddingLeft: '18px',
                          background: selected ? activeBg : panel,
                        }}
                      >
                        <span>{metric.label}</span>
                        {selected ? <Check size={14} color={ui.interactive} /> : <span style={{ width: 14 }} />}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}

          {hasFilters ? (
            <div style={sectionBorderTop(hasAxis || hasDimensions || hasMetrics)}>
              <button
                type="button"
                role="menuitem"
                onClick={() => toggleExpanded('filter')}
                style={itemBtn(expanded === 'filter')}
              >
                <span>Filtrar</span>
                <ChevronRight
                  size={14}
                  color={muted}
                  style={{
                    transform: expanded === 'filter' ? 'rotate(90deg)' : 'none',
                    transition: 'transform 0.15s ease',
                  }}
                />
              </button>
              {expanded === 'filter' ? (
                <div role="menu" style={{ borderTop: `1px solid ${border}` }}>
                  {filterSections.map((section, sectionIndex) => {
                    const sectionKey = section.key || section.title || String(sectionIndex);
                    const sectionOpen = isFilterSectionOpen(sectionKey);
                    return (
                      <div key={sectionKey}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '8px',
                            padding: '6px 10px',
                            fontSize: '10px',
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            color: muted,
                            background: headerBg,
                            borderTop: sectionIndex > 0 ? `1px solid ${border}` : 'none',
                            borderBottom: `1px solid ${border}`,
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => toggleFilterSection(sectionKey)}
                            aria-expanded={sectionOpen}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              border: 'none',
                              background: 'transparent',
                              color: muted,
                              fontSize: '10px',
                              fontWeight: 700,
                              letterSpacing: '0.04em',
                              textTransform: 'uppercase',
                              cursor: 'pointer',
                              padding: 0,
                              minWidth: 0,
                              flex: 1,
                              textAlign: 'left',
                            }}
                          >
                            <ChevronRight
                              size={12}
                              style={{
                                flexShrink: 0,
                                transform: sectionOpen ? 'rotate(90deg)' : 'none',
                                transition: 'transform 0.15s ease',
                              }}
                            />
                            <span
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {section.title}
                            </span>
                          </button>
                          {!isNumericRangeSection(section) ? (
                            <span style={{ display: 'inline-flex', gap: '6px', flexShrink: 0 }}>
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  section.onShowAll?.();
                                }}
                                style={{
                                  border: 'none',
                                  background: 'transparent',
                                  color: ui.interactive,
                                  fontSize: '10px',
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  padding: 0,
                                }}
                              >
                                Todas
                              </button>
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  section.onHideAll?.();
                                }}
                                style={{
                                  border: 'none',
                                  background: 'transparent',
                                  color: muted,
                                  fontSize: '10px',
                                  fontWeight: 700,
                                  cursor: 'pointer',
                                  padding: 0,
                                }}
                              >
                                Ninguna
                              </button>
                            </span>
                          ) : null}
                        </div>
                        {sectionOpen ? (
                          isNumericRangeSection(section) ? (
                            renderNumericRangeControls(section)
                          ) : (
                            (section.options || []).map((option) => {
                              const checked = section.visibility?.[option.key] !== false;
                              return (
                                <label
                                  key={option.key}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '7px 10px 7px 18px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: text,
                                    cursor: 'pointer',
                                    background: panel,
                                    borderBottom: `1px solid color-mix(in srgb, ${border} 70%, transparent)`,
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => section.onToggle?.(option.key)}
                                    style={{ margin: 0, accentColor: ui.interactive }}
                                  />
                                  {option.color ? (
                                    <span
                                      style={{
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '2px',
                                        background: option.color,
                                        flexShrink: 0,
                                      }}
                                    />
                                  ) : null}
                                  <span
                                    style={{
                                      flex: 1,
                                      minWidth: 0,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    {option.label}
                                  </span>
                                </label>
                              );
                            })
                          )
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default ChartOptionsMenu;
